import { useState } from 'react';
import type { MediaKind } from '../../shared/api/types';

/**
 * Subcarpetas de Multimedia en la consola (Fondos, Videolyrics…), por tipo de archivo. Se guardan
 * en esta computadora, como los archivos de EasyWorship: cada archivo recuerda en qué carpeta está
 * por su clave («local:<nombre>» o el id del recurso de la iglesia).
 */
type Store = { folders: Partial<Record<MediaKind, string[]>>; assign: Record<string, string> };
const KEY = 'keora.consola.carpetas';

function load(): Store {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Store>;
    return { folders: raw.folders ?? {}, assign: raw.assign ?? {} };
  } catch {
    return { folders: {}, assign: {} };
  }
}

export const cleanFolder = (name: string) => name.replace(/\s+/g, ' ').trim().slice(0, 60);

export function useMediaFolders() {
  const [store, setStore] = useState<Store>(load);
  const save = (change: (s: Store) => Store) =>
    setStore((prev) => {
      const next = change(prev);
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        // Sin almacenamiento: duran mientras la consola esté abierta.
      }
      return next;
    });

  const list = (kind: MediaKind) => store.folders[kind] ?? [];
  /** Carpeta de un archivo (solo si la carpeta todavía existe para su tipo). */
  const folderOf = (key: string, kind: MediaKind) => {
    const f = store.assign[`${kind}:${key}`];
    return f && list(kind).includes(f) ? f : null;
  };

  function create(kind: MediaKind, name: string) {
    const n = cleanFolder(name);
    if (!n) throw new Error('Escribe el nombre de la carpeta.');
    if (list(kind).some((f) => f.toLowerCase() === n.toLowerCase())) throw new Error(`Ya hay una carpeta «${n}».`);
    save((s) => ({ ...s, folders: { ...s.folders, [kind]: [...(s.folders[kind] ?? []), n].sort((a, b) => a.localeCompare(b, 'es')) } }));
    return n;
  }

  function rename(kind: MediaKind, from: string, to: string) {
    const n = cleanFolder(to);
    if (!n) throw new Error('Escribe el nombre de la carpeta.');
    if (n !== from && list(kind).some((f) => f.toLowerCase() === n.toLowerCase())) throw new Error(`Ya hay una carpeta «${n}».`);
    save((s) => ({
      folders: { ...s.folders, [kind]: (s.folders[kind] ?? []).map((f) => (f === from ? n : f)).sort((a, b) => a.localeCompare(b, 'es')) },
      assign: Object.fromEntries(Object.entries(s.assign).map(([k, f]) => [k, k.startsWith(`${kind}:`) && f === from ? n : f])),
    }));
    return n;
  }

  /** Quitar la carpeta: sus archivos no se borran, vuelven a «Todos». */
  function remove(kind: MediaKind, name: string) {
    save((s) => ({
      folders: { ...s.folders, [kind]: (s.folders[kind] ?? []).filter((f) => f !== name) },
      assign: Object.fromEntries(Object.entries(s.assign).filter(([k, f]) => !(k.startsWith(`${kind}:`) && f === name))),
    }));
  }

  function move(key: string, kind: MediaKind, folder: string | null) {
    save((s) => {
      const assign = { ...s.assign };
      if (folder) assign[`${kind}:${key}`] = folder;
      else delete assign[`${kind}:${key}`];
      return { ...s, assign };
    });
  }

  /** Un archivo local cambió de nombre: su carpeta se va con él. */
  function rekey(from: string, to: string, kind: MediaKind) {
    const f = store.assign[`${kind}:${from}`];
    if (!f || from === to) return;
    save((s) => {
      const assign = { ...s.assign, [`${kind}:${to}`]: f };
      delete assign[`${kind}:${from}`];
      return { ...s, assign };
    });
  }

  return { list, folderOf, create, rename, remove, move, rekey };
}
